import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { parseISO } from 'date-fns';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import {
  classifyCopyFailure,
  discardCopyAttempt,
  getCopyAttempt,
  saveCopyAttempt,
  useCopyWorkOrder,
  type CopyAttempt,
  type CopyWorkOrderBody,
  type CopyWorkOrderMode,
} from '@/hooks/useCopyWorkOrder';
import { useNotify } from '@/hooks/useNotify';
import { describeApiError } from '@/lib/describeApiError';
import { navigateAwayFromWorkOrderDetail } from '@/lib/workOrderNavigation';
import { useAuthStore } from '@/stores/authStore';

/** Local civil date, `YYYY-MM-DD`. The default planned day, never the source schedule. */
function todayCivilDate(now = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${String(year)}-${month}-${day}`;
}

/**
 * Floating day as UTC midnight. Same encoding as
 * `create/requestTimes.floatingScheduledAtIso` (`YYYY-MM-DDT00:00:00.000Z`).
 */
function floatingDayIso(civilDate: string): string {
  const day = civilDate.split('T')[0] ?? civilDate;
  return `${day}T00:00:00.000Z`;
}

function isCivilDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/** Local midnight of a civil date, so a pickup is compared to the day, not a clock time. */
function civilDateStart(value: string): Date {
  return new Date(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10)));
}

interface CopyWorkOrderDialogProps {
  open: boolean;
  workOrderId: string;
  workOrderNumber: string;
  vehicleId: string;
  /** The vehicle's recorded mileage, shown as information only: it is never put in the input. */
  vehicleMileage: number | null;
  onClose: () => void;
}

interface FieldErrors {
  scheduledAt?: string;
  pickupAt?: string;
  dropoffAt?: string;
  mileage?: string;
}

/** The work order is the new quote's source only; the form below starts empty on purpose. */
export function CopyWorkOrderDialog({
  open,
  workOrderId,
  workOrderNumber,
  vehicleId,
  vehicleMileage,
  onClose,
}: CopyWorkOrderDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const navigate = useNavigate();
  const location = useLocation();
  const copy = useCopyWorkOrder(workOrderId, vehicleId);
  const userId = useAuthStore((state) => state.user?.id);
  const tenantId = useAuthStore((state) => state.user?.tenantId);
  const scope = { tenantId, userId };

  // An attempt that was sent and not settled outlives this dialog (it is only
  // mounted while open), so closing and reopening cannot hand out a new key.
  const [attempt, setAttemptState] = useState<CopyAttempt | null>(() => getCopyAttempt(scope, workOrderId));

  const [mode, setMode] = useState<CopyWorkOrderMode>(attempt?.form.mode ?? 'all');
  // The planned day defaults to today and is never the source work order's date. Mileage stays blank.
  const [scheduledAt, setScheduledAt] = useState(attempt?.form.scheduledAt ?? todayCivilDate());
  const [pickupAt, setPickupAt] = useState(attempt?.form.pickupAt ?? '');
  const [dropoffAt, setDropoffAt] = useState(attempt?.form.dropoffAt ?? '');
  const [mileage, setMileage] = useState(attempt?.form.mileage ?? '');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [failure, setFailure] = useState<string | null>(null);

  // One key and one body per attempt. While an attempt is unsettled (network or
  // server failure, still in progress, outcome unknown) the form is locked and
  // every send replays that stored key and body, so a retry cannot make a
  // second quote. Only a definite refusal of a FIRST send releases the key: a
  // refusal of a replay says nothing about the send that came before it.
  const submittingRef = useRef(false);
  const busy = copy.isPending;
  const locked = attempt !== null;
  const unresolved = attempt?.phase === 'unresolved';
  const blocked = attempt?.phase === 'blocked';
  const inputsDisabled = busy || locked;

  const setAttempt = (next: CopyAttempt | null) => {
    if (next) saveCopyAttempt(scope, workOrderId, next);
    else discardCopyAttempt(scope, workOrderId);
    setAttemptState(next);
  };

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    const typed = scheduledAt.trim();
    // Optional (#4116). A cleared day means today; only a malformed day is an error.
    if (typed !== '' && !isCivilDate(typed)) next.scheduledAt = t('copyToQuote.scheduledDateInvalid');
    const plannedDay = typed === '' || !isCivilDate(typed) ? todayCivilDate() : typed;
    const scheduled = isCivilDate(plannedDay) ? civilDateStart(plannedDay) : null;
    if (pickupAt) {
      const pickup = parseISO(pickupAt);
      if (Number.isNaN(pickup.getTime())) next.pickupAt = t('copyToQuote.pickupInvalid');
      else if (scheduled && !next.scheduledAt && pickup < scheduled) {
        next.pickupAt = t('copyToQuote.pickupBeforeSchedule');
      }
    }
    if (dropoffAt && Number.isNaN(parseISO(dropoffAt).getTime())) next.dropoffAt = t('copyToQuote.dropoffInvalid');
    if (mileage.trim() !== '') {
      const value = Number(mileage);
      if (!Number.isInteger(value) || value < 1 || value > 9_999_999) next.mileage = t('copyToQuote.mileageInvalid');
    }
    return next;
  };

  const buildBody = (): CopyWorkOrderBody => {
    const day = isCivilDate(scheduledAt) ? scheduledAt : todayCivilDate();
    return {
      mode,
      scheduledAt: floatingDayIso(day),
      schedulingType: 'floating',
      ...(pickupAt ? { customerRequestedPickupAt: parseISO(pickupAt).toISOString() } : {}),
      ...(dropoffAt ? { vehicleDropoffAt: parseISO(dropoffAt).toISOString() } : {}),
      ...(mileage.trim() !== '' ? { mileage: Number(mileage) } : {}),
    };
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (busy || submittingRef.current || unresolved) return;

    let sending: CopyAttempt;
    if (attempt) {
      // Replay exactly what was sent before; whatever the inputs hold now is ignored.
      sending = { ...attempt, phase: 'sending' };
    } else {
      const found = validate();
      setErrors(found);
      if (Object.keys(found).length > 0) return;
      sending = {
        idempotencyKey: crypto.randomUUID(),
        body: buildBody(),
        form: { mode, scheduledAt, pickupAt, dropoffAt, mileage },
        phase: 'sending',
      };
    }

    submittingRef.current = true;
    setFailure(null);
    // Stored before the send: if this dialog goes away mid-flight, the next one
    // replays this key instead of minting a new one.
    setAttempt(sending);
    copy.mutate(
      { body: sending.body, idempotencyKey: sending.idempotencyKey },
      {
        onSuccess: (result) => {
          submittingRef.current = false;
          setAttempt(null);
          const description = [
            t('copyToQuote.createdDesc', {
              services: result.summary.servicesCopied,
              items: result.summary.itemsCopied + result.summary.stockPartsCopiedAsItems,
            }),
            result.summary.fixedPriceServicesPartlyDone > 0 ? t('copyToQuote.fixedPriceNote') : null,
          ]
            .filter(Boolean)
            .join(' ');
          notify.success(t('copyToQuote.created', { number: result.workOrderNumber ?? '' }), description);
          navigateAwayFromWorkOrderDetail(location.pathname, `/workorders/${result.id}`, navigate);
          onClose();
        },
        onError: (error) => {
          submittingRef.current = false;
          // `attempt` is what was stored before this click. If it exists, an
          // earlier send (lost, unanswered, or from a dialog that went away) may
          // have committed, and this answer cannot prove it did not.
          const kind = classifyCopyFailure(error, { priorOutcomeUnknown: attempt !== null });
          if (kind === 'rejected') {
            // A first send was refused: definitely nothing was created, release
            // the key. The form keeps everything the user typed; only the
            // message is added.
            setAttempt(null);
            setFailure(
              describeApiError(error, tc, {
                codePrefix: 'workorders:copyToQuote.errorCode',
                fallback: t('copyToQuote.error'),
              }),
            );
            return;
          }
          setAttempt({ ...sending, phase: kind });
        },
      },
    );
  };

  /** The user's own decision, after checking the quote list, to risk a separate copy. */
  const startNewCopy = () => {
    setAttempt(null);
    setFailure(null);
  };

  const submitLabel = t('copyToQuote.submit');
  const attemptMessage = attempt
    ? t(
        attempt.phase === 'unresolved'
          ? 'copyToQuote.unresolved'
          : attempt.phase === 'in_progress'
            ? 'copyToQuote.inProgress'
            : attempt.phase === 'blocked'
              ? 'copyToQuote.blocked'
              : 'copyToQuote.uncertain',
        { action: submitLabel },
      )
    : null;
  // While sending, the alert would only repeat what the spinner says.
  const notice = attempt?.phase === 'sending' && busy ? null : attemptMessage;

  return (
    <ResponsiveDialog
      open={open}
      onClose={busy ? undefined : onClose}
      mobileTitle={t('copyToQuote.title')}
      maxWidth="sm"
      fullWidth
    >
      <Box component="form" noValidate onSubmit={handleSubmit}>
        <DialogTitle>{t('copyToQuote.title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('copyToQuote.intro', { number: workOrderNumber })}
          </Typography>

          {(notice ?? failure) && (
            <Alert severity={locked ? 'warning' : 'error'} role="alert">
              {notice ?? failure}
            </Alert>
          )}

          <FormControl disabled={inputsDisabled}>
            <FormLabel id="copy-mode-label">{t('copyToQuote.modeLabel')}</FormLabel>
            <RadioGroup
              aria-labelledby="copy-mode-label"
              value={mode}
              onChange={(event) => {
                if (!locked) setMode(event.target.value as CopyWorkOrderMode);
              }}
            >
              <FormControlLabel value="all" control={<Radio />} label={t('copyToQuote.modeAll')} />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: -0.5, mb: 0.5 }}>
                {t('copyToQuote.modeAllHint')}
              </Typography>
              <FormControlLabel value="unfinished" control={<Radio />} label={t('copyToQuote.modeUnfinished')} />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: -0.5 }}>
                {t('copyToQuote.modeUnfinishedHint')}
              </Typography>
            </RadioGroup>
          </FormControl>

          <TextField
            label={t('copyToQuote.scheduledAt')}
            type="date"
            value={scheduledAt}
            onChange={(event) => {
              if (!locked) setScheduledAt(event.target.value);
            }}
            size="small"
            disabled={inputsDisabled}
            error={!!errors.scheduledAt}
            helperText={errors.scheduledAt ?? t('copyToQuote.scheduledAtHint')}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={t('copyToQuote.pickupAt')}
            type="datetime-local"
            value={pickupAt}
            onChange={(event) => {
              if (!locked) setPickupAt(event.target.value);
            }}
            size="small"
            disabled={inputsDisabled}
            error={!!errors.pickupAt}
            helperText={errors.pickupAt}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={t('copyToQuote.dropoffAt')}
            type="datetime-local"
            value={dropoffAt}
            onChange={(event) => {
              if (!locked) setDropoffAt(event.target.value);
            }}
            size="small"
            disabled={inputsDisabled}
            error={!!errors.dropoffAt}
            helperText={errors.dropoffAt}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={t('copyToQuote.mileage')}
            type="number"
            value={mileage}
            onChange={(event) => {
              if (!locked) setMileage(event.target.value);
            }}
            size="small"
            disabled={inputsDisabled}
            error={!!errors.mileage}
            helperText={errors.mileage ?? t('copyToQuote.mileageHint')}
            slotProps={{ htmlInput: { min: 1, step: 1 } }}
          />
          {vehicleMileage != null && (
            <Typography variant="caption" color="text.secondary">
              {t('copyToQuote.vehicleMileageNote', { mileage: vehicleMileage.toLocaleString('fi-FI') })}
            </Typography>
          )}

          <Alert severity="info" icon={false} role="note">
            <Typography variant="subtitle2">{t('copyToQuote.keptTitle')}</Typography>
            <Typography variant="body2">{t('copyToQuote.kept')}</Typography>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              {t('copyToQuote.clearedTitle')}
            </Typography>
            <Typography variant="body2">{t('copyToQuote.cleared')}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t('copyToQuote.notCopied')}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            {tc('cancel')}
          </Button>
          {(unresolved || blocked) && (
            <Button onClick={startNewCopy} variant="outlined" color="warning" disabled={busy}>
              {t('copyToQuote.startNew')}
            </Button>
          )}
          {!unresolved && (
            <Button
              type="submit"
              variant="contained"
              disabled={busy}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {busy ? t('copyToQuote.submitting') : submitLabel}
            </Button>
          )}
        </DialogActions>
      </Box>
    </ResponsiveDialog>
  );
}
